import Color from 'color';
import DOMPurify from 'dompurify';

import { isValidCssColor } from 'components/text_editor_new/lib/should_allow_element_color';
import { MAX_TABLE_COLUMNS } from 'components/text_editor_new/lib/table_constants';

/**
 * Cleans Team Assist answer HTML before it's shown, inserted into the composer
 * (Use), or copied. Model output can repeat text planted in a customer's
 * conversation, so this allows the tags the Answer editor writes
 * (`text_editor_new/lib/serialize_html.js`, `serialize_html_text.js`) plus a
 * few safe structural extras (paragraphs, headings, rules, quotes, table
 * sections), and nothing else:
 *
 * - **Tags:** those above; anything else is dropped with its text kept, except
 *   active content, which goes entirely.
 * - **Styles** are rebuilt, never passed through: `color`, `font-size` (bounded,
 *   in units that don't compound when nested), cell `background-color`, and
 *   `text-align`, each validated. Nothing that positions, sizes, or paints an
 *   element survives, so no overlays. Text colors stay readable on the panel,
 *   but a cell background can still match the color of its text.
 * - **Images** only from Gladly: the current org's `shared-uploads` path or the
 *   public-asset CDN over https. An image loads with no click, so any other
 *   host would let an injected `<img>` report back to whoever planted it.
 *   Loosening this is only safe while neither location redirects elsewhere or
 *   holds customer-uploaded files.
 * - **Links** are absolute https (`https://`), mailto, or tel, and always open
 *   in a new tab.
 * - **No classes:** presentation comes from the panel, never the model. With
 *   `allowCallouts` (never for a draft, which goes to the customer), a `div`
 *   keeps `data-callout` with a known kind, which the panel styles.
 *
 * Uses its own DOMPurify instance, so its hooks never touch `SafeHtml`'s.
 */
export default function sanitizeTeamAssistHtml(html, { orgId, cdnHost, allowCallouts = false }) {
  currentCall = { orgId, cdnHost, allowCallouts };
  try {
    return purify.sanitize(html || '', CONFIG);
  } finally {
    currentCall = null;
  }
}

const purify = DOMPurify(window);

// DOMPurify runs synchronously, so the current call's options (the image rule,
// whether callouts are allowed) can sit here for the hooks to read.
let currentCall = null;

const CONFIG = {
  ALLOWED_TAGS: [
    'div',
    'p',
    'br',
    'hr',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'b',
    'i',
    'u',
    's',
    'strong',
    'em',
    'span',
    'ul',
    'ol',
    'li',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'caption',
    'a',
    'img',
  ],
  ALLOWED_ATTR: [
    'href',
    'target',
    'rel',
    'src',
    'alt',
    'data-attachment-id',
    'data-file',
    'colspan',
    'rowspan',
    'dir',
    'style',
    'data-callout',
  ],
  ALLOW_DATA_ATTR: false,
  // Absolute https/mailto/tel, or a root-relative path (never protocol-relative).
  // The hooks narrow this further: links take no relative paths, images only
  // the image rule's.
  ALLOWED_URI_REGEXP: /^(?:(?:https|mailto|tel):|\/(?!\/))/i,
  // Not URLs, so not checked against the URI pattern above; the hooks validate
  // each of them instead. The image ones go into the composer on Use, whose
  // serializer writes them back into HTML, so they matter as much as `src`.
  ADD_URI_SAFE_ATTR: ['dir', 'colspan', 'rowspan', 'data-attachment-id', 'data-file', 'data-callout'],
  ADD_FORBID_CONTENTS: ['form', 'button', 'select', 'textarea', 'object', 'embed'],
};

const COLOR_TAGS = new Set(['SPAN', 'DIV', 'LI', 'TD', 'TH']);
const BACKGROUND_TAGS = new Set(['TD', 'TH']);
const ALIGN_TAGS = new Set(['DIV', 'LI', 'TD', 'TH']);
const DIR_TAGS = new Set(['DIV', 'UL', 'OL', 'LI']);
const SPAN_TAGS = new Set(['TD', 'TH']);
const TEXT_ALIGNS = new Set(['left', 'right', 'center', 'justify', 'start', 'end']);
const DIRS = new Set(['auto', 'ltr', 'rtl']);
// The kinds the panel draws as a callout box; see `renderCallouts`.
export const CALLOUT_KINDS = new Set(['recommendation', 'warning']);
// `https:` without `//` would resolve against the page, as a same-origin path.
const LINK_SCHEMES = /^(?:https:\/\/|mailto:|tel:)/i;
const ATTACHMENT_ID = /^[\w-]{1,64}$/;
// Characters a file name can't hold: any that could close or escape an
// attribute, or start a path.
const FILE_NAME_FORBIDDEN = /["'<>&\\/]/;
const FILE_NAME_MAX_LENGTH = 255;
const UNSAFE_CSS_VALUE = /url\(|var\(|expression\(|!|\\|\/\*/i;
// Bounds keep pasted text readable without letting it cover the panel.
// Only units that don't compound: nested `em` or `%` spans multiply.
const FONT_SIZE_BOUNDS = { px: [8, 32], pt: [6, 24], rem: [0.5, 2] };

purify.addHook('uponSanitizeAttribute', (node, data) => {
  const { attrName, attrValue } = data;
  switch (attrName) {
    case 'style': {
      const style = rebuildStyle(node.nodeName, attrValue);
      if (style) {
        data.attrValue = style;
      } else {
        data.keepAttr = false;
      }
      return;
    }
    case 'colspan':
    case 'rowspan':
      data.keepAttr = SPAN_TAGS.has(node.nodeName) && isTableSpan(attrValue);
      return;
    case 'data-callout':
      data.keepAttr =
        Boolean(currentCall && currentCall.allowCallouts) &&
        node.nodeName === 'DIV' &&
        CALLOUT_KINDS.has(attrValue.trim());
      return;
    case 'dir':
      data.keepAttr = DIR_TAGS.has(node.nodeName) && DIRS.has(attrValue.trim().toLowerCase());
      return;
    case 'href':
      data.keepAttr = node.nodeName === 'A' && LINK_SCHEMES.test(attrValue.trim());
      return;
    case 'src':
      data.keepAttr = node.nodeName === 'IMG' && isGladlyImage(attrValue.trim());
      return;
    case 'data-attachment-id':
      data.keepAttr = node.nodeName === 'IMG' && ATTACHMENT_ID.test(attrValue);
      return;
    case 'data-file':
      data.keepAttr = node.nodeName === 'IMG' && isFileName(attrValue);
      return;
    case 'target':
    case 'rel':
      // Set on every link after sanitizing; never taken from the input.
      data.keepAttr = false;
      return;
    default:
      return;
  }
});

purify.addHook('afterSanitizeAttributes', node => {
  if (node.nodeName === 'IMG' && !node.hasAttribute('src')) {
    node.remove();
    return;
  }
  if (node.nodeName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

/** The allowed declarations of a `style` value for this element, or '' if none survive. */
function rebuildStyle(nodeName, styleValue) {
  const kept = [];
  styleValue.split(';').forEach(declaration => {
    const colon = declaration.indexOf(':');
    if (colon === -1) {
      return;
    }
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (!value || UNSAFE_CSS_VALUE.test(value)) {
      return;
    }
    if (isAllowedDeclaration(nodeName, property, value)) {
      kept.push(`${property}:${value}`);
    }
  });
  return kept.join(';');
}

function isAllowedDeclaration(nodeName, property, value) {
  switch (property) {
    case 'color':
      return COLOR_TAGS.has(nodeName) && isVisibleTextColor(value);
    case 'background-color':
      return BACKGROUND_TAGS.has(nodeName) && isOpaqueColor(value);
    case 'font-size':
      return nodeName === 'SPAN' && isBoundedFontSize(value);
    case 'text-align':
      return ALIGN_TAGS.has(nodeName) && TEXT_ALIGNS.has(value.toLowerCase());
    default:
      return false;
  }
}

// Opaque, and neither near-black nor near-white, so text in it reads on the
// panel in light and dark mode alike.
function isVisibleTextColor(value) {
  if (!isValidCssColor(value)) {
    return false;
  }
  const color = Color(value);
  const luminosity = color.luminosity();
  return color.alpha() === 1 && luminosity > 0.05 && luminosity < 0.9;
}

function isOpaqueColor(value) {
  return isValidCssColor(value) && Color(value).alpha() === 1;
}

function isFileName(value) {
  return (
    value.length > 0 &&
    value.length <= FILE_NAME_MAX_LENGTH &&
    !FILE_NAME_FORBIDDEN.test(value) &&
    !hasControlOrSpace(value.replace(/ /g, ''))
  );
}

// Whether `text` holds whitespace or a control character. In a URL these
// matter because the URL parser drops tabs and line breaks, so the string
// checked wouldn't be the URL the browser requests.
function hasControlOrSpace(text) {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) {
      return true;
    }
  }
  return false;
}

function isBoundedFontSize(value) {
  const match = /^(\d+(?:\.\d+)?)(px|pt|rem)$/i.exec(value);
  if (!match) {
    return false;
  }
  const [min, max] = FONT_SIZE_BOUNDS[match[2].toLowerCase()];
  const size = Number(match[1]);
  return size >= min && size <= max;
}

function isTableSpan(value) {
  const span = Number(value);
  return Number.isInteger(span) && span >= 1 && span <= MAX_TABLE_COLUMNS;
}

/**
 * An Answer image Gladly serves: the current org's `shared-uploads` path, or
 * the public-asset CDN over https. Nothing else, not even other same-origin
 * paths, since a same-origin image is a GET sent with the agent's cookies.
 */
function isGladlyImage(src) {
  if (!currentCall || !src || hasControlOrSpace(src) || !isDecodable(src)) {
    return false;
  }
  const { orgId, cdnHost } = currentCall;
  const sharedUploads = orgId && `/api/v1/orgs/${encodeURIComponent(orgId)}/shared-uploads/`;
  if (sharedUploads && src.startsWith(sharedUploads)) {
    // No traversal in any spelling (`..`, encoded dots or slashes,
    // backslashes), judged on the path the browser will request.
    if (/\.\.|%2e|%2f|%5c|\\/i.test(src)) {
      return false;
    }
    const url = new URL(src, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith(sharedUploads);
  }
  // Absolute only: `https:/host/…` or `https:host/…` resolve against the page,
  // so they'd be a same-origin request. Resolved as the browser will.
  if (!cdnHost || !/^https:\/\//i.test(src)) {
    return false;
  }
  try {
    const url = new URL(src, window.location.href);
    return url.protocol === 'https:' && url.host === new URL(cdnHost).host;
  } catch {
    return false;
  }
}

// The composer's image plugin decodes `src` on Use, and a malformed escape
// there throws.
function isDecodable(src) {
  try {
    decodeURI(src);
    return true;
  } catch {
    return false;
  }
}
